package com.sddamico.draftmachine.pages.draft

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.components.layouts.LoadingFrame
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.components.sections.AppHeader
import com.sddamico.draftmachine.components.widgets.SegmentedTabs
import com.sddamico.draftmachine.data.DraftStatus
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.core.Card
import com.sddamico.draftmachine.ds.core.CardVariant
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmPanel
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.Input
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.forms.Select
import com.sddamico.draftmachine.ds.forms.Switch
import com.sddamico.draftmachine.ds.motion.Motion
import com.sddamico.draftmachine.ds.motion.ViewTransitions
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleH2
import com.sddamico.draftmachine.models.DraftDetailResponse
import com.sddamico.draftmachine.models.PendingPickInfo
import com.sddamico.draftmachine.models.QuietHoursSettings
import com.sddamico.draftmachine.models.ReviewPickRequest
import com.sddamico.draftmachine.models.SuccessResponse
import com.sddamico.draftmachine.pages.league.LeagueConnectionPanel
import com.sddamico.draftmachine.util.QUIET_HOURS_ZONES
import com.sddamico.draftmachine.util.formatMinuteOfDay
import com.sddamico.draftmachine.util.minuteOfDayToTimeValue
import com.sddamico.draftmachine.util.timeValueToMinuteOfDay
import com.sddamico.draftmachine.util.transitionTo
import com.sddamico.draftmachine.util.zoneLabel
import com.sddamico.draftmachine.util.UNREACHABLE_MESSAGE
import com.sddamico.draftmachine.util.CancelDraftResult
import kotlinx.serialization.json.JsonElement
import com.sddamico.draftmachine.util.LoadState
import com.sddamico.draftmachine.util.cancelDraft
import com.sddamico.draftmachine.util.UpdateDraftSettingsResult
import com.sddamico.draftmachine.util.updateDraftSettings
import com.sddamico.draftmachine.util.UpdateDraftPickNotificationsResult
import com.sddamico.draftmachine.util.updateDraftPickNotifications
import com.sddamico.draftmachine.util.SetDraftAdminsResult
import com.sddamico.draftmachine.util.setDraftAdmins
import com.sddamico.draftmachine.util.rememberAuthState
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option

@Page("{id}/settings")
@Composable
fun DraftSettingsPage() {
  val ctx = rememberPageContext()
  val scope = rememberCoroutineScope()
  val auth = rememberAuthState()
  val draftId = ctx.route.params["id"].orEmpty()
  val settingsPath = "/draft/$draftId/settings"
  val magic = rememberMagicLink(scope)

  if (MagicLinkGate(magic, onLogin = { ctx.router.transitionTo("/login?returnTo=$settingsPath") })) return

  when (val authState = auth.value) {
    is LoadState.Loading -> LoadingFrame()
    is LoadState.Failed -> {
      LaunchedEffect(Unit) { ctx.router.transitionTo("/login") }
      LoadingFrame()
    }
    is LoadState.Ready -> {
      val user = authState.value
      if (user == null) {
        if (magic.available) {
          LaunchedEffect(magic.token) { magic.consume() }
        } else {
          LaunchedEffect(Unit) { ctx.router.transitionTo("/login?returnTo=$settingsPath") }
        }
        LoadingFrame()
      } else {
        var detailState by remember(draftId) {
          mutableStateOf<LoadState<DraftDetailResponse>>(LoadState.Loading)
        }
        LaunchedEffect(draftId) {
          magic.settle()
          val loaded = fetchDraftDetail(
            draftId,
            forbiddenMessage = "You're not on this draft's crew, so the settings stay locked.",
          )
          ViewTransitions.run(Motion.Fade) { detailState = loaded }
        }

        when (val detail = detailState) {
          is LoadState.Loading -> LoadingFrame()
          is LoadState.Failed -> DraftSettingsError(
            email = user.email,
            message = detail.message,
            onBack = { ctx.router.transitionTo("/draft/$draftId") },
          )
          is LoadState.Ready -> DraftSettings(
            detail = detail.value,
            email = user.email,
            onBack = { ctx.router.transitionTo("/draft/$draftId") },
            onChanged = {
              scope.launch { detailState = fetchDraftDetail(draftId) }
            },
          )
        }
      }
    }
  }
}

@Composable
private fun DraftSettingsError(email: String, message: String, onBack: () -> Unit) {
  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column),
    header = { AppHeader(email = email, title = "Draft settings", onBack = onBack) },
  ) {
    Div(
      attrs = Modifier.flex(1).displayFlex().flexDirection(FlexDirection.Column)
        .alignItems(AlignItems.Center).justifyContent(JustifyContent.Center).gap(14.px)
        .padding(32.px).toAttrs(),
    ) {
      Icon("wrench", IconWeight.Bold, Modifier.fontSize(40.px).color(DmColor.steel600))
      SpanText(
        message,
        Modifier.roleH2().color(DmColor.textSecondary)
          .styleModifier { property("text-align", "center") },
      )
      Button("Back to draft", variant = ButtonVariant.Secondary, icon = "arrow-left", onClick = onBack)
    }
  }
}

@Composable
private fun DraftSettings(
  detail: DraftDetailResponse,
  email: String,
  onBack: () -> Unit,
  onChanged: () -> Unit,
) {
  val scope = rememberCoroutineScope()
  var tab by remember { mutableStateOf(SettingsTab.Settings) }
  var showConfirmation by remember { mutableStateOf(false) }
  var cancelling by remember { mutableStateOf(false) }
  var error by remember { mutableStateOf<String?>(null) }
  var pickNotificationsEnabled by remember(detail.draft.id, detail.pickNotificationsEnabled) {
    mutableStateOf(detail.pickNotificationsEnabled)
  }
  var savingPickNotifications by remember { mutableStateOf(false) }
  var pickNotificationsError by remember { mutableStateOf<String?>(null) }
  var timerEnabled by remember(detail.draft.id, detail.draft.pickTimerMinutes) {
    mutableStateOf(detail.draft.pickTimerMinutes != null)
  }
  var timerHours by remember(detail.draft.id, detail.draft.pickTimerMinutes) {
    mutableStateOf(detail.draft.pickTimerMinutes?.let { (it / 60.0).toString().removeSuffix(".0") } ?: "8")
  }
  var savingTimer by remember { mutableStateOf(false) }
  var timerError by remember { mutableStateOf<String?>(null) }
  var quietHours by remember(detail.draft.id, detail.draft.quietHours) {
    mutableStateOf(detail.draft.quietHours)
  }
  var savingQuietHours by remember { mutableStateOf(false) }
  var quietHoursError by remember { mutableStateOf<String?>(null) }
  var adminTeamIds by remember(detail.draft.id, detail.draftAdminTeamIds) {
    mutableStateOf(detail.draftAdminTeamIds.toSet())
  }
  var savingAdmins by remember { mutableStateOf(false) }
  var adminError by remember { mutableStateOf<String?>(null) }
  var adminToAdd by remember(detail.draft.id, detail.draftAdminTeamIds) { mutableStateOf("") }

  /** Persists the overnight pause; the timer length rides along unchanged from the loaded draft. */
  fun saveQuietHours(next: QuietHoursSettings) {
    if (savingQuietHours) return
    val previous = quietHours
    quietHours = next
    quietHoursError = null
    scope.launch {
      savingQuietHours = true
      when (val result = updateDraftSettings(detail.draft.id, detail.draft.pickTimerMinutes, next)) {
        is UpdateDraftSettingsResult.Success -> onChanged()
        is UpdateDraftSettingsResult.Failure -> {
          quietHours = previous
          quietHoursError = result.message
        }
      }
      savingQuietHours = false
    }
  }

  fun saveAdminTeams(next: Set<String>) {
    if (savingAdmins) return
    val previous = adminTeamIds
    adminTeamIds = next
    adminError = null
    scope.launch {
      savingAdmins = true
      when (val result = setDraftAdmins(detail.draft.id, next.toList())) {
        is SetDraftAdminsResult.Success -> {
          adminToAdd = ""
          onChanged()
        }
        is SetDraftAdminsResult.Failure -> {
          adminTeamIds = previous
          adminError = result.message
        }
      }
      savingAdmins = false
    }
  }

  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column),
    header = { AppHeader(email = email, title = "Draft settings", onBack = onBack) },
  ) {
    Div(attrs = Modifier.padding(DmSpacing.space6).toAttrs()) {
      SpanText("Draft settings", Modifier.roleH2().displayBlock().margin(bottom = DmSpacing.space2))
      SpanText(
        detail.leagueName ?: "Manage this draft and its machinery.",
        Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(bottom = DmSpacing.space6),
      )

      // Off-pool pick awaiting a ruling (pools frame 1j) — shown above everything else.
      detail.pendingPick?.let { pending ->
        ReviewPickPanel(detail, pending, onRuled = onChanged)
        Div(attrs = Modifier.height(DmSpacing.space5).toAttrs()) {}
      }

      if (detail.youCanManage) {
        SegmentedTabs(
          SettingsTab.entries.map { it.label },
          tab.ordinal,
          Modifier.margin(bottom = DmSpacing.space6),
        ) { ViewTransitions.run(Motion.Update) { tab = SettingsTab.entries[it] } }
      }

      if (detail.youCanManage && tab == SettingsTab.Teams) {
        DraftTeamsTab(detail.draft.id, onSaved = onChanged)
      } else {
        if (detail.youManage && detail.leagueId != null) {
          LeagueConnectionPanel(detail.leagueId, requiredSport = detail.pool?.sport)
          Div(attrs = Modifier.height(DmSpacing.space6).toAttrs()) {}
        }

        Card(variant = CardVariant.Raised, riveted = true) {
          Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(DmSpacing.space3).toAttrs()) {
            Icon("bell-ringing", IconWeight.Bold, Modifier.fontSize(24.px).color(DmColor.glass400))
            Div(attrs = Modifier.toAttrs()) {
              SpanText("Notifications", Modifier.roleH2().displayBlock())
            }
          }

          Div(
            attrs = Modifier.height(1.px).backgroundColor(DmColor.border)
              .margin(topBottom = DmSpacing.space5).toAttrs(),
          ) {}

          SpanText("Every pick made", Modifier.dmStamp().color(DmColor.brass300).displayBlock())
          SpanText(
            "Get an email and browser notification whenever a pick lands. If you're next, you'll only get the usual on-the-clock alert.",
            Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(topBottom = DmSpacing.space3),
          )
          Switch(
            label = "Notify me about every pick",
            checked = pickNotificationsEnabled,
            disabled = savingPickNotifications ||
              detail.draft.status == DraftStatus.COMPLETE || detail.draft.status == DraftStatus.CANCELLED,
            onChange = { enabled ->
              val previous = pickNotificationsEnabled
              pickNotificationsEnabled = enabled
              pickNotificationsError = null
              scope.launch {
                savingPickNotifications = true
                when (val result = updateDraftPickNotifications(detail.draft.id, enabled)) {
                  is UpdateDraftPickNotificationsResult.Success -> {
                    pickNotificationsEnabled = result.enabled
                    onChanged()
                  }
                  is UpdateDraftPickNotificationsResult.Failure -> {
                    pickNotificationsEnabled = previous
                    pickNotificationsError = result.message
                  }
                }
                savingPickNotifications = false
              }
            },
          )
          pickNotificationsError?.let {
            SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(top = DmSpacing.space3))
          }
          if (detail.draft.status == DraftStatus.COMPLETE || detail.draft.status == DraftStatus.CANCELLED) {
            SpanText(
              "Notification settings are locked because this draft has ended.",
              Modifier.roleBody().color(DmColor.textMuted).displayBlock().margin(top = DmSpacing.space3),
            )
          }
        }

        Div(attrs = Modifier.height(DmSpacing.space5).toAttrs()) {}

        Card(variant = CardVariant.Raised, riveted = true) {
          Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(DmSpacing.space3).toAttrs()) {
            Icon("gear", IconWeight.Bold, Modifier.fontSize(24.px).color(DmColor.brass400))
            Div(attrs = Modifier.toAttrs()) {
              SpanText("Draft controls", Modifier.roleH2().displayBlock())
            }
          }

          Div(
            attrs = Modifier.height(1.px).backgroundColor(DmColor.border)
              .margin(topBottom = DmSpacing.space5).toAttrs(),
          ) {}

          if (detail.youManage) {
            SpanText("Draft admins", Modifier.dmStamp().color(DmColor.brass300).displayBlock())
            SpanText(
              "Draft admins can manage the draft, but cannot assign admins or cancel it.",
              Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(topBottom = DmSpacing.space3),
            )
            val availableTeams = detail.teams.filter {
              it.id in detail.draftAdminEligibleTeamIds && it.id !in adminTeamIds
            }
            Select(
              label = "Add draft admin",
              hint = if (availableTeams.isEmpty()) "No eligible teams available." else null,
              value = adminToAdd,
              onValueChange = { teamId ->
                adminToAdd = teamId
                if (teamId.isNotEmpty()) saveAdminTeams(adminTeamIds + teamId)
              },
            ) {
              Option(value = "") { org.jetbrains.compose.web.dom.Text("Select a team…") }
              availableTeams.forEach { team ->
                Option(value = team.id) {
                  org.jetbrains.compose.web.dom.Text("${team.teamName} · ${team.ownerName}")
                }
              }
            }
            SpanText("Current admins", Modifier.dmStamp().color(DmColor.textMuted).displayBlock().margin(top = DmSpacing.space5, bottom = DmSpacing.space2))
            val currentAdmins = detail.teams.filter { it.id in adminTeamIds }
            if (currentAdmins.isEmpty()) {
              SpanText("No draft admins assigned.", Modifier.roleBody().color(DmColor.textSecondary))
            } else {
              Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(DmSpacing.space2).toAttrs()) {
                currentAdmins.forEach { team ->
                  Div(
                    attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
                      .justifyContent(JustifyContent.SpaceBetween).gap(DmSpacing.space3)
                      .padding(DmSpacing.space3).dmPanel()
                      .styleModifier { property("box-shadow", "none") }.toAttrs(),
                  ) {
                    SpanText("${team.teamName} · ${team.ownerName}", Modifier.roleBody().color(DmColor.text))
                    IconButton(
                      icon = "x",
                      label = "Remove ${team.teamName} from draft admins",
                      variant = ButtonVariant.Ghost,
                      size = ButtonSize.Sm,
                      disabled = savingAdmins,
                      onClick = { saveAdminTeams(adminTeamIds - team.id) },
                    )
                  }
                }
              }
            }
            adminError?.let {
              SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(top = DmSpacing.space3))
            }
            Div(
              attrs = Modifier.height(1.px).backgroundColor(DmColor.border)
                .margin(topBottom = DmSpacing.space5).toAttrs(),
            ) {}
          }

          if (detail.youCanManage && detail.draft.status != DraftStatus.COMPLETE && detail.draft.status != DraftStatus.CANCELLED) {
            SpanText("Pick timer", Modifier.dmStamp().color(DmColor.brass300).displayBlock())
            SpanText(
              "Automatically pass a team when its time expires. Missed picks remain available.",
              Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(topBottom = DmSpacing.space3),
            )
            Switch(
              label = "Use a pick timer",
              checked = timerEnabled,
              disabled = savingTimer,
              onChange = { enabled ->
                timerError = null
                val previous = timerEnabled
                timerEnabled = enabled
                scope.launch {
                  savingTimer = true
                  val minutes = if (enabled) 480 else null
                  when (val result = updateDraftSettings(detail.draft.id, minutes)) {
                    is UpdateDraftSettingsResult.Success -> {
                      if (enabled) timerHours = "8"
                      onChanged()
                    }
                    is UpdateDraftSettingsResult.Failure -> {
                      timerEnabled = previous
                      timerError = result.message
                    }
                  }
                  savingTimer = false
                }
              },
            )
            if (timerEnabled) {
              Div(attrs = Modifier.margin(top = DmSpacing.space4).toAttrs()) {
                Input(
                  label = "Hours per pick",
                  hint = "Enter 0.25–168 hours.",
                  suffix = "hr",
                  type = "number",
                  value = timerHours,
                  onValueChange = { timerHours = it; timerError = null },
                  error = timerError,
                )
              }
            } else if (timerError != null) {
              SpanText(timerError!!, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(top = DmSpacing.space3))
            }
            if (timerEnabled) {
              Button(
                "Save timer length",
                variant = ButtonVariant.Primary,
                icon = "floppy-disk",
                loading = savingTimer,
                modifier = Modifier.margin(top = DmSpacing.space4),
                onClick = {
                  val hours = timerHours.toDoubleOrNull()
                  val minutes = if (timerEnabled && hours != null) kotlin.math.round(hours * 60).toInt() else null
                  when {
                    timerEnabled && (hours == null || !hours.isFinite() || hours !in 0.25..168.0 || minutes !in 15..10_080) ->
                      timerError = "Enter a timer from 0.25 to 168 hours."
                    else -> scope.launch {
                      savingTimer = true
                      when (val result = updateDraftSettings(detail.draft.id, minutes)) {
                        is UpdateDraftSettingsResult.Success -> onChanged()
                        is UpdateDraftSettingsResult.Failure -> timerError = result.message
                      }
                      savingTimer = false
                    }
                  }
                },
              )
            }
            if (timerEnabled) {
              QuietHoursControls(
                quietHours = quietHours,
                saving = savingQuietHours,
                error = quietHoursError,
                onSave = ::saveQuietHours,
              )
            }
            Div(
              attrs = Modifier.height(1.px).backgroundColor(DmColor.border)
                .margin(topBottom = DmSpacing.space5).toAttrs(),
            ) {}
          }

          when {
            !detail.youCanManage -> SpanText(
              "Only the league manager or a draft admin can change draft settings.",
              Modifier.roleBody().color(DmColor.textSecondary),
            )
            detail.youManage && detail.draft.status == DraftStatus.ACTIVE -> {
              SpanText("Cancel draft", Modifier.dmStamp().color(DmColor.rust300).displayBlock())
              SpanText(
                "Stop the draft and skip every remaining pick. Picks already made will be preserved.",
                Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).lineHeight(1.55)
                  .color(DmColor.textSecondary).displayBlock().margin(topBottom = DmSpacing.space3),
              )
              error?.let {
                SpanText(
                  it,
                  Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(bottom = DmSpacing.space3),
                )
              }
              Button(
                "Cancel draft",
                variant = ButtonVariant.Danger,
                icon = "prohibit",
                onClick = { error = null; showConfirmation = true },
              )
            }
            detail.youManage -> SpanText(
              statusMessage(detail.draft.status),
              Modifier.roleBody().color(DmColor.textSecondary),
            )
            else -> SpanText(
              "Draft admins cannot cancel the draft.",
              Modifier.roleBody().color(DmColor.textSecondary),
            )
          }
        }
      }
    }
  }

  if (showConfirmation && detail.youManage && detail.draft.status == DraftStatus.ACTIVE) {
    CancelDraftConfirmation(
      detail = detail,
      cancelling = cancelling,
      onDismiss = { if (!cancelling) showConfirmation = false },
      onConfirm = {
        if (!cancelling) scope.launch {
          cancelling = true
          when (val result = cancelDraft(detail.draft.id)) {
            is CancelDraftResult.Success -> {
              showConfirmation = false
              onChanged()
            }
            is CancelDraftResult.Failure -> {
              showConfirmation = false
              error = result.message
            }
          }
          cancelling = false
        }
      },
    )
  }
}

/**
 * Overnight pause for the pick timer. Toggling it on saves immediately with the current window;
 * editing the window or zone is committed by the save button, so a half-typed time never lands.
 * Shown only while a pick timer is set — it has nothing to pause otherwise.
 */
@Composable
private fun QuietHoursControls(
  quietHours: QuietHoursSettings,
  saving: Boolean,
  error: String?,
  onSave: (QuietHoursSettings) -> Unit,
) {
  // Draft edits stay local until saved; a fresh server value (or a rolled-back save) reseeds them.
  var startMinute by remember(quietHours) { mutableStateOf(quietHours.startMinute) }
  var endMinute by remember(quietHours) { mutableStateOf(quietHours.endMinute) }
  var zone by remember(quietHours) { mutableStateOf(quietHours.zone) }
  var localError by remember(quietHours) { mutableStateOf<String?>(null) }

  val windowLength = if (endMinute > startMinute) endMinute - startMinute else 1440 - startMinute + endMinute
  /** Same rules the server enforces, so a bad window is caught before the round trip. */
  fun windowError(): String? = when {
    startMinute == endMinute -> "Quiet hours must start and end at different times."
    windowLength > 16 * 60 -> "The overnight pause can't run longer than 16 hours."
    else -> null
  }

  /** Commits whatever window is on screen, so a pending edit isn't silently dropped. */
  fun commit(enabled: Boolean) {
    val error = if (enabled) windowError() else null
    if (error != null) localError = error
    else onSave(QuietHoursSettings(enabled, startMinute, endMinute, zone))
  }

  Div(attrs = Modifier.margin(top = DmSpacing.space5).toAttrs()) {
    SpanText("Overnight pause", Modifier.dmStamp().color(DmColor.brass300).displayBlock())
    SpanText(
      "Hold the clock while everyone's asleep. Time inside the window doesn't count against a pick, " +
        "so nobody gets passed overnight.",
      Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(topBottom = DmSpacing.space3),
    )
    Switch(
      label = "Pause the clock overnight",
      checked = quietHours.enabled,
      disabled = saving,
      onChange = { enabled -> localError = null; commit(enabled) },
    )
    if (quietHours.enabled) {
      Div(
        attrs = Modifier.displayFlex().gap(DmSpacing.space3).margin(top = DmSpacing.space4).toAttrs(),
      ) {
        // The DS Input only syncs its DOM value at mount, so key on the server value: a save that
        // rolls back remounts the field instead of stranding the rejected text in it.
        Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
          key(quietHours) {
            Input(
              label = "Pause from",
              type = "time",
              value = minuteOfDayToTimeValue(startMinute),
              onValueChange = { value ->
                timeValueToMinuteOfDay(value)?.let { startMinute = it }
                localError = null
              },
            )
          }
        }
        Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
          key(quietHours) {
            Input(
              label = "Until",
              type = "time",
              value = minuteOfDayToTimeValue(endMinute),
              onValueChange = { value ->
                timeValueToMinuteOfDay(value)?.let { endMinute = it }
                localError = null
              },
            )
          }
        }
      }
      Div(attrs = Modifier.margin(top = DmSpacing.space4).toAttrs()) {
        Select(
          label = "Time zone",
          value = zone,
          onValueChange = { zone = it; localError = null },
        ) {
          QUIET_HOURS_ZONES.forEach { (id, label) ->
            Option(value = id) { org.jetbrains.compose.web.dom.Text(label) }
          }
          // A zone set outside this list (or by an older client) stays selectable rather than
          // silently switching the draft to Eastern on the next save.
          if (QUIET_HOURS_ZONES.none { it.first == zone }) {
            Option(value = zone) { org.jetbrains.compose.web.dom.Text(zone) }
          }
        }
      }
      SpanText(
        windowError() ?: "The clock holds from ${formatMinuteOfDay(startMinute)} to " +
          "${formatMinuteOfDay(endMinute)} ${zoneLabel(zone)}" +
          "${if (endMinute <= startMinute) ", overnight into the next day" else ""}.",
        Modifier.roleBody().fontSize(DmType.textXs).color(DmColor.textMuted)
          .displayBlock().margin(top = DmSpacing.space3),
      )
      (localError ?: error)?.let {
        SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(top = DmSpacing.space3))
      }
      Button(
        "Save overnight pause",
        variant = ButtonVariant.Primary,
        icon = "moon-stars",
        loading = saving,
        modifier = Modifier.margin(top = DmSpacing.space4),
        onClick = { localError = null; commit(true) },
      )
    } else {
      (localError ?: error)?.let {
        SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(top = DmSpacing.space3))
      }
    }
  }
}

/**
 * The pause notice + ruling card for a pending off-pool pick: approve locks it in and restarts
 * the wheel; sending it back puts the team back on the clock.
 */
@Composable
private fun ReviewPickPanel(
  detail: DraftDetailResponse,
  pending: PendingPickInfo,
  onRuled: () -> Unit,
) {
  val scope = rememberCoroutineScope()
  var ruling by remember { mutableStateOf(false) }
  var rulingError by remember { mutableStateOf<String?>(null) }
  val pickLabel = remember(detail.picks, pending.position) {
    pickLabels(detail.picks)[pending.position] ?: pending.position.toString()
  }

  fun rule(approve: Boolean) {
    if (ruling) return
    ruling = true
    rulingError = null
    scope.launch {
      try {
        apiPost(
          "/api/draft-pick-review",
          apiJson.encodeToString(
            ReviewPickRequest.serializer(),
            ReviewPickRequest(detail.draft.id, pending.position, approve),
          ),
          JsonElement.serializer(),
        )
        onRuled()
      } catch (e: ApiException) {
        rulingError = e.errorMessage
      } catch (e: Throwable) {
        rulingError = UNREACHABLE_MESSAGE
      }
      ruling = false
    }
  }

  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.FlexStart).gap(DmSpacing.space3).padding(14.px)
      .borderRadius(DmSpacing.space2)
      .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, DmColor.warning)
      .backgroundColor(Color.rgba(232, 194, 74, 0.07f))
      .margin(bottom = DmSpacing.space4).toAttrs()
  ) {
    Icon("pause-circle", IconWeight.Fill, Modifier.fontSize(20.px).color(DmColor.warning).margin(top = 1.px))
    Div {
      SpanText(
        "Draft paused — pick $pickLabel awaits a ruling",
        Modifier.fontFamily(*DmType.heading).fontSize(14.px).fontWeight(DmType.weightBold)
          .color(DmColor.text).displayBlock(),
      )
      SpanText(
        "No pick emails go out until a manager rules.",
        Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textMuted)
          .displayBlock().margin(top = 2.px),
      )
    }
  }

  Card(variant = CardVariant.Raised, riveted = true) {
    Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(DmSpacing.space4).toAttrs()) {
      SpanText(
        pickLabel,
        Modifier.fontFamily(*DmType.mono).fontSize(26.px).fontWeight(DmType.weightBold).color(DmColor.rust400),
      )
      Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
        SpanText(pending.playerText, Modifier.roleH2().displayBlock())
        SpanText(
          "ENTERED BY ${pending.teamName.uppercase()}" +
            (pending.enteredAt?.let { " · ${com.sddamico.draftmachine.util.formatStampDateTime(it)}" } ?: ""),
          Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted)
            .displayBlock().margin(top = 3.px),
        )
      }
    }
    Div(
      attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(DmSpacing.space2)
        .padding(topBottom = DmSpacing.space3, leftRight = DmSpacing.space3)
        .margin(topBottom = DmSpacing.space4)
        .borderRadius(DmSpacing.space2)
        .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, DmColor.rust500)
        .backgroundColor(Color.rgba(180, 71, 42, 0.08f)).toAttrs()
    ) {
      Icon("magnifying-glass-minus", IconWeight.Bold, Modifier.fontSize(16.px).color(DmColor.rust300))
      SpanText(
        "NOT FOUND IN ${(detail.pool?.name ?: "THE POOL").uppercase()}",
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.rust300),
      )
    }
    if (detail.youCanManage) {
      Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(DmSpacing.space2).toAttrs()) {
        Button(
          "Approve the pick", variant = ButtonVariant.Primary, icon = "check", fullWidth = true,
          loading = ruling, onClick = { rule(true) },
        )
        Button(
          "Send it back to re-pick", variant = ButtonVariant.Secondary, icon = "arrow-u-up-left", fullWidth = true,
          disabled = ruling, onClick = { rule(false) },
        )
      }
      rulingError?.let {
        SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(top = DmSpacing.space3))
      }
      SpanText(
        "Approving locks $pickLabel and restarts the draft. Sending it back puts ${pending.teamName} back on the clock.",
        Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textMuted)
          .lineHeight(1.5).displayBlock().margin(top = DmSpacing.space3),
      )
    } else {
      SpanText(
        "Only the league manager or a draft admin can rule on this pick.",
        Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(top = DmSpacing.space3),
      )
    }
  }
}

private enum class SettingsTab(val label: String) {
  Settings("Settings"),
  Teams("Teams"),
}

private fun statusMessage(status: DraftStatus): String = when (status) {
  DraftStatus.SETUP -> "This draft has not started yet, so it cannot be cancelled."
  DraftStatus.ACTIVE -> ""
  DraftStatus.COMPLETE -> "This draft is complete. Its settings are locked."
  DraftStatus.CANCELLED -> "This draft has already been cancelled."
}

@Composable
private fun CancelDraftConfirmation(
  detail: DraftDetailResponse,
  cancelling: Boolean,
  onDismiss: () -> Unit,
  onConfirm: () -> Unit,
) {
  val remaining = detail.picks.count { !it.resolved }
  Div(
    attrs = Modifier.position(Position.Fixed).zIndex(1000).displayFlex()
      .alignItems(AlignItems.Center).justifyContent(JustifyContent.Center)
      .padding(20.px).styleModifier { property("inset", "0") }
      .toAttrs {
        attr("role", "dialog")
        attr("aria-modal", "true")
        attr("aria-labelledby", "cancel-draft-title")
      },
  ) {
    Div(
      attrs = Modifier.position(Position.Absolute).backgroundColor(Color.rgba(13, 16, 19, 0.72f))
        .styleModifier { property("inset", "0") }.toAttrs { onClick { onDismiss() } },
    ) {}
    Div(
      attrs = Modifier.position(Position.Relative).zIndex(1).fillMaxWidth().maxWidth(390.px)
        .dmPanel().padding(20.px).displayFlex().flexDirection(FlexDirection.Column).gap(14.px).toAttrs(),
    ) {
      Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px).toAttrs()) {
        Icon("warning", IconWeight.Fill, Modifier.fontSize(28.px).color(DmColor.rust400))
        Div(attrs = Modifier.toAttrs { id("cancel-draft-title") }) {
          SpanText("Cancel this draft?", Modifier.roleH2().displayBlock())
        }
      }
      SpanText(
        "This will stop the draft immediately and skip the $remaining remaining " +
          "pick${if (remaining == 1) "" else "s"}. Picks already made will be preserved. This can't be undone.",
        Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.textSecondary).lineHeight(1.55),
      )
      Div(attrs = Modifier.displayFlex().gap(10.px).toAttrs()) {
        Button(
          "Keep drafting",
          variant = ButtonVariant.Secondary,
          disabled = cancelling,
          onClick = onDismiss,
          modifier = Modifier.flex(1),
        )
        Button(
          "Cancel draft",
          variant = ButtonVariant.Danger,
          icon = "prohibit",
          loading = cancelling,
          onClick = onConfirm,
          modifier = Modifier.flex(1),
        )
      }
    }
  }
}
